import React, { lazy, Suspense } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'

import { Layout, FederationErrorBoundary } from '../components/Layout'
import PageLoading from '@client/components/PageLoading'
import { RequireAuth } from '@cooltrax/jellyfish-sdk'
import megalodonShadow from 'react-shadow'
import { usePermissions } from '@client/hooks/usePermissions'

import { Alert } from '@cooltrax/jellyfish-ui'

const FreshRoutes = lazy(() => import('jellyfresh/freshRoutes'))
const MegalodonRoutes = lazy(() => import('megalodon/megalodonRoutes'))
const ErrorPage = lazy(() => import('../pages/ErrorPage'))
const MaintenancePage = lazy(() => import('../pages/MaintenancePage'))
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'))
const ProfilePage = lazy(() => import('../pages/ProfilePage'))
const SettingsPage = lazy(() => import('../pages/Settings/SettingsPage'))
const HelpPage = lazy(() => import('../pages/Help/HelpPage'))
const PrivacyPage = lazy(() => import('../pages/Privacy/PrivacyPage'))
const EditProfile = lazy(() => import('../pages/ProfilePage/EditProfile'))

// AuthWrapper component to handle authentication logic
const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RequireAuth>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </RequireAuth>
  )
}

// PermissionWrapper component to handle permission-based routing
const PermissionWrapper: React.FC<{
  children: React.ReactNode
  permission: string
  fallback?: React.ReactNode
}> = ({ children, permission, fallback }) => {
  const { hasPermission } = usePermissions()

  if (!hasPermission(permission)) {
    return fallback ? <>{fallback}</> : <Alert type="error" message="You do not have permissions for this page." />
  }

  return <>{children}</>
}

// Component to handle default route based on permissions
const DefaultRouteHandler: React.FC = () => {
  const { getFirstAvailableRoute } = usePermissions()
  const firstAvailableRoute = getFirstAvailableRoute()

  return <Navigate to={firstAvailableRoute} replace />
}

export const createRoutes = (): RouteObject[] => [
  {
    path: '/',
    element: <Layout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          <AuthWrapper>
            <DefaultRouteHandler />
          </AuthWrapper>
        ),
      },
      {
        path: 'fresh/*',
        element: (
          <AuthWrapper>
            <FederationErrorBoundary>
              <FreshRoutes />
            </FederationErrorBoundary>
          </AuthWrapper>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'insights/*',
        element: (
          <AuthWrapper>
            <PermissionWrapper
              permission="insights:r"
              fallback={<Alert type="error" message="You do not have permissions for this page." />}
            >
              <FederationErrorBoundary>
                <megalodonShadow.div>
                  <MegalodonRoutes />
                </megalodonShadow.div>
              </FederationErrorBoundary>
            </PermissionWrapper>
          </AuthWrapper>
        ),
        errorElement: <ErrorPage />,
      },
      {
        path: 'help',
        element: (
          <AuthWrapper>
            <HelpPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'privacy',
        element: (
          <AuthWrapper>
            <PrivacyPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <AuthWrapper>
            <ProfilePage />
          </AuthWrapper>
        ),
      },
      {
        path: 'profile/edit',
        element: (
          <AuthWrapper>
            <EditProfile />
          </AuthWrapper>
        ),
      },
      {
        path: 'settings',
        element: (
          <AuthWrapper>
            <SettingsPage />
          </AuthWrapper>
        ),
      },
      {
        path: '*',
        element: (
          <AuthWrapper>
            <NotFoundPage />
          </AuthWrapper>
        ),
        errorElement: <ErrorPage />,
      },
    ],
  },
  {
    path: 'maintenance',
    element: <MaintenancePage reason="" downtimeInHour="" />,
  },
]

export default createRoutes
